
import { reactive, SetupContext, computed, onMounted } from 'vue'
import Login from '@/views/Login.vue'
import ApplyHotelAbstract from '@/components/block/ApplyHotelAbstract.vue'
import Button from '@/components/elements/Button.vue'
import HeadingLv3 from '@/components/elements/HeadingLv3.vue'
import FormGroup from '@/components/block/FormGroup.vue'
import FormFieldset from '@/components/block/FormFieldset.vue'
import FormInputField from '@/components/block/FormInputField.vue'
import FormSelectField from '@/components/block/FormSelectField.vue'
import FormTextareaField from '@/components/block/FormTextareaField.vue'
import CheckboxButtons from '@/components/block/FormCheckboxButtons.vue'
import PreviewPairData from '@/components/block/PreviewPairData.vue'
import GoalButtons from '@/components/block/GoalButtons.vue'
import TextLink from '@/components/elements/TextLink.vue'
import ModalInputCaution from '@/components/block/ModalInputCaution.vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { applyValidation } from '@/utils/validation'
import axios from 'axios'

export default {
  name: 'VisitApplicationInput',

  components: {
    Login,
    Button,
    HeadingLv3,
    FormGroup,
    FormFieldset,
    FormInputField,
    FormTextareaField,
    FormSelectField,
    PreviewPairData,
    GoalButtons,
    CheckboxButtons,
    TextLink,
    ModalInputCaution,
    ApplyHotelAbstract
  },

  props: {
    state: {
      type: Object,
      require: true
    },
    data: {
      type: Object,
      default: () => ({})
    },
    notEnterdData: {
      type: Object,
      default: () => ({})
    },
    err: {
      type: Object,
      default: () => ({})
    }
  },

  setup(props: any, context: SetupContext) {
    const route = useRoute()
    const router = useRouter()
    const store = useStore()

    // 申し込みフローの更新
    context.emit('update-flow', 1)

    // 入力ページから入ってきているステータスを設定
    context.emit('update-landing-state', true)

    // ------------------------------------------------------
    // バリデーション設定
    // ------------------------------------------------------
    interface ValidationsObject {
      [key: string]: any
    }

    const validations: ValidationsObject = reactive({
      preferred_date: {
        rules: ['required', 'max:50'],
        isError: false,
        errorMessage: ''
      },
      preferred_note: {
        rules: ['max:255'],
        isError: false,
        errorMessage: ''
      },
      zip: {
        rules: ['required', 'zip'],
        isError: false,
        errorMessage: ''
      },
      prefecture: {
        rules: ['required', 'select'],
        isError: false,
        errorMessage: ''
      },
      city: {
        rules: ['required', 'max:255'],
        isError: false,
        errorMessage: ''
      },
      town: {
        rules: ['required', 'max:255'],
        isError: false,
        errorMessage: ''
      },
      tel: {
        rules: ['required', 'max:500', 'hankakueisu'],
        isError: false,
        errorMessage: ''
      },
      zip_vacation: {
        rules: ['required', 'zip'],
        isError: false,
        errorMessage: ''
      },
      prefecture_vacation: {
        rules: ['required', 'select'],
        isError: false,
        errorMessage: ''
      },
      city_vacation: {
        rules: ['required', 'max:255'],
        isError: false,
        errorMessage: ''
      },
      town_vacation: {
        rules: ['required', 'max:255'],
        isError: false,
        errorMessage: ''
      }
    })

    // 内容確認ボタン
    const clickConfirmBtn = () => {
      router.push({
        path: `/visit_application/${route.params.id}/confirm`
      })
    }

    const changeMessage = (name: string, value: string) => {
      context.emit('update-data', name, value)

      const result = applyValidation(validations[name].rules, value)

      validations[name].isError = result.isError
      validations[name].errorMessage = result.message
    }

    const changeVacationAddressState = (ev: Record<string, boolean>) => {
      context.emit('update-data', 'vacation_flg', ev.checked)

      const arr = [
        'zip_vacation',
        'prefecture_vacation',
        'city_vacation',
        'town_vacation'
      ]

      for (let i = 0; i < arr.length; i++) {
        if (ev.checked) {
          validations[arr[i]].isError = false
        } else {
          const result = applyValidation(
            validations[arr[i]].rules,
            props.notEnterdData[arr[i]]
          )
          validations[arr[i]].isError = result.isError
          validations[arr[i]].errorMessage = result.message
        }
      }
    }
    const checkErrorDefaultInputData = () => {
      // 住所データ、電話番号が未入力
      if (
        (!props.data.user.address && props.notEnterdData.zip === '') ||
        (!props.data.user.address && props.notEnterdData.prefecture === '') ||
        (!props.data.user.address && props.notEnterdData.city === '') ||
        (!props.data.user.address && props.notEnterdData.town === '') ||
        (!props.data.user.tel.content && props.notEnterdData.tel === '')
      ) {
        return true
      }
      // 休暇中住所が住所と同じでないが、休暇中住所が未入力
      if (
        !props.data.user.address_vacation &&
        !props.notEnterdData.vacation_flg
      ) {
        if (
          props.notEnterdData.zip_vacation === '' ||
          props.notEnterdData.prefecture_vacation === '' ||
          props.notEnterdData.city_vacation === '' ||
          props.notEnterdData.town_vacation === ''
        ) {
          return true
        }
      }
      return false
    }

    // すでに保持している登録情報にエラーがあるか
    // 確認画面から戻って再修正する場合を考慮して、エラーが有る場合は一度しかstateの値を更新しない
    const isDefaultError = () => {
      if (props.state.isErrorDefaultInputData) return true
      return checkErrorDefaultInputData()
    }
    context.emit('update-error-default-input-data', isDefaultError())

    // 確認ボタンの活性・非活性制御
    const isDisabledNextBtn = computed(() => {
      if (props.data.contents.preferred_date.content === '') {
        return true
      }

      if (checkErrorDefaultInputData()) return true

      const arr: Array<string> = Object.keys(validations)

      for (let i = 0; i < arr.length; i++) {
        if (validations[arr[i]].isError) return true
      }

      return false
    })

    interface SelectObject {
      label: string
      value: string
      selected: boolean
    }
    const selectDefault = {
      label: '選択してください',
      value: '',
      selected: false
    }
    const prefectures: Array<SelectObject> = reactive([selectDefault])
    const prefectures_vacation: Array<SelectObject> = reactive([selectDefault])
    const selectedPref = props.notEnterdData.prefecture
      ? props.notEnterdData.prefecture
      : 0
    const selectedPrefVacation = props.notEnterdData.prefecture_vacation
      ? props.notEnterdData.prefecture_vacation
      : 0

    store.state.master.prefectures.forEach((el: any) => {
      if (el.opt_id === '0') return

      const item: SelectObject = {
        label: el.option_name,
        value: el.opt_id,
        selected: selectedPref === el.opt_id ? true : false
      }
      const item2: SelectObject = {
        label: el.option_name,
        value: el.opt_id,
        selected: selectedPrefVacation === el.opt_id ? true : false
      }
      prefectures.push(item)
      prefectures_vacation.push(item2)
    })

    // 郵便番号から住所取得 @TODO 共通化できそうならする
    const getAddressFromZipCode = (name: string, value: string) => {
      const zip1 = value.substr(0, 3)
      const zip2 = value.substr(3, 4)

      axios
        .get(
          `https://madefor.github.io/postal-code-api/api/v1/${zip1}/${zip2}.json`
        )
        .then((res) => {
          let selectedPref = 0

          for (let i = 0; i < prefectures.length; i++) {
            if (
              Number(res.data.data[0].prefcode) === Number(prefectures[i].value)
            ) {
              selectedPref = i
            }
          }

          if (name === 'zip') {
            prefectures[selectedPref].selected = true
            changeMessage('prefecture', prefectures[selectedPref].value)
            changeMessage('city', res.data.data[0].ja.address1)
            changeMessage('town', res.data.data[0].ja.address2)
          }
          if (name === 'zip_vacation') {
            prefectures_vacation[selectedPref].selected = true
            changeMessage(
              'prefecture_vacation',
              prefectures_vacation[selectedPref].value
            )
            changeMessage('city_vacation', res.data.data[0].ja.address1)
            changeMessage('town_vacation', res.data.data[0].ja.address2)
          }
        })
    }

    const inputZip = (name: string, value: string) => {
      changeMessage(name, value)

      if (/^[0-9]{7}$/.test(value)) {
        getAddressFromZipCode(name, value)
      }
    }

    const showPrevBtn = window.history.state.back ? true : false

    // サーバーサイドのバリデーションエラーで戻って来た時
    onMounted(() => {
      if (Object.keys(props.err).length > 0) {
        setTimeout(() => {
          window.scrollTo({
            top: 0,
            behavior: 'auto'
          })
        }, 100)

        Object.keys(props.err).forEach((key: string) => {
          validations[key].isError = true
          validations[key].errorMessage = props.err[key][0]
        })
      }
    })

    return {
      validations,
      changeMessage,
      changeVacationAddressState,
      prefectures,
      prefectures_vacation,
      inputZip,
      isDisabledNextBtn,
      clickConfirmBtn,
      showPrevBtn
    }
  }
}
